import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ATID } from "@/lib/adminTestIds";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ScrollText, RefreshCcw, Loader2, X } from "lucide-react";
import { formatDateTime } from "@/lib/date";

const ACTION_LABELS = {
  login: "Inicio de sesión",
  logout: "Cierre de sesión",
  create_article: "Creó una noticia",
  update_article: "Editó una noticia",
  publish_article: "Publicó una noticia",
  submit_article: "Envió a revisión",
  archive_article: "Archivó una noticia",
  trash_article: "Envió a papelera",
  duplicate_article: "Duplicó una noticia",
  delete_article: "Eliminó definitivamente",
  article_publish: "Publicó",
  article_pending: "Envió a revisión",
  article_draft: "Volvió a borrador",
  article_unpublish: "Despublicó",
  article_archive: "Archivó",
  article_trash: "Envió a papelera",
  article_restore: "Restauró",
  article_schedule: "Programó publicación",
  article_toggle_featured: "Cambió destacada",
  article_toggle_breaking: "Cambió último momento",
  create_category: "Creó categoría",
  update_category: "Editó categoría",
  delete_category: "Eliminó categoría",
  reorder_categories: "Reordenó categorías",
  create_homepage_block: "Creó bloque de portada",
  update_homepage_block: "Editó bloque de portada",
  delete_homepage_block: "Eliminó bloque de portada",
  reorder_homepage: "Reordenó la portada",
  set_homepage_block_articles: "Actualizó artículos de bloque",
  create_campaign: "Creó campaña",
  update_campaign: "Editó campaña",
  delete_campaign: "Eliminó campaña",
  create_ad_creative: "Creó publicidad",
  update_ad_creative: "Editó publicidad",
  delete_ad_creative: "Eliminó publicidad",
  ad_creative_activate: "Activó publicidad",
  ad_creative_pause: "Pausó publicidad",
  ad_creative_reset_counters: "Reseteó contadores",
  create_ad_slot: "Creó ubicación publicitaria",
  update_ad_slot: "Editó ubicación publicitaria",
  delete_ad_slot: "Eliminó ubicación publicitaria",
  update_settings: "Actualizó configuración",
  create_user: "Creó usuario",
  update_user: "Editó usuario",
  delete_user: "Eliminó usuario",
  reset_password: "Restableció contraseña",
  change_password: "Cambió su contraseña",
  close_sessions: "Cerró sesiones",
  password_reset_request: "Solicitó restablecimiento",
  delete_media: "Eliminó archivo multimedia",
};

const RESOURCE_LABELS = {
  article: "Noticia",
  category: "Categoría",
  homepage_block: "Portada",
  campaign: "Campaña",
  ad_creative: "Publicidad",
  ad_slot: "Ubicación publicitaria",
  user: "Usuario",
  settings: "Configuración",
  media: "Multimedia",
};

const RESOURCES = ["article", "category", "homepage_block", "campaign", "ad_creative", "ad_slot", "user", "settings", "media"];

const EMPTY_FILTERS = {
  q: "", action: "", resource_type: "", user_id: "",
  date_from: "", date_to: "", resource_id: "",
};

export default function AdminAuditPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const pageSize = 50;

  useEffect(() => {
    api.adminListUsers({ limit: 500 }).then((data) => {
      setUsers(data?.items || []);
    }).catch(() => {});
  }, []);

  const reload = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { limit: pageSize, skip: targetPage * pageSize };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const data = await api.adminListAudit(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("No se pudo cargar el historial");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(page); }, [page]);

  const applyFilters = () => {
    setPage(0);
    reload(0);
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPage(0);
    setTimeout(() => reload(0), 0);
  };

  const availableActions = useMemo(() => Object.keys(ACTION_LABELS).sort(), []);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div data-testid={ATID.auditPage} className="max-w-7xl">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-textgray">Panel</div>
          <h1 className="text-3xl font-black text-nightblue mt-1 font-serif flex items-center gap-2">
            <ScrollText className="w-6 h-6" /> Auditoría
          </h1>
          <p className="text-sm text-textgray mt-1">
            Historial de actividad: quién creó, editó, aprobó, publicó o eliminó contenido.
          </p>
        </div>
        <Button onClick={() => reload(page)} variant="outline">
          <RefreshCcw className="w-4 h-4 mr-2" /> Actualizar
        </Button>
      </header>

      <div className="bg-white border border-softblue p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Buscar</Label>
            <Input
              data-testid={ATID.auditFilterQ}
              placeholder="Email, título, ID de noticia/campaña…"
              value={filters.q}
              onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Usuario</Label>
            <Select
              value={filters.user_id || "all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, user_id: v === "all" ? "" : v }))}
            >
              <SelectTrigger data-testid={ATID.auditFilterUser}><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los usuarios</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Acción</Label>
            <Select
              value={filters.action || "all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, action: v === "all" ? "" : v }))}
            >
              <SelectTrigger data-testid={ATID.auditFilterAction}><SelectValue placeholder="Todas" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las acciones</SelectItem>
                {availableActions.map((a) => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Tipo de contenido</Label>
            <Select
              value={filters.resource_type || "all"}
              onValueChange={(v) => setFilters((f) => ({ ...f, resource_type: v === "all" ? "" : v }))}
            >
              <SelectTrigger data-testid={ATID.auditFilterResource}><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {RESOURCES.map((r) => (
                  <SelectItem key={r} value={r}>{RESOURCE_LABELS[r] || r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">ID de noticia / campaña</Label>
            <Input
              data-testid={ATID.auditFilterResourceId}
              placeholder="Ej: uuid…"
              value={filters.resource_id}
              onChange={(e) => setFilters((f) => ({ ...f, resource_id: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Desde</Label>
            <Input
              data-testid={ATID.auditFilterDateFrom}
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-textgray mb-1 block">Hasta</Label>
            <Input
              data-testid={ATID.auditFilterDateTo}
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={clearFilters} data-testid={ATID.auditReset} disabled={activeFilterCount === 0}>
            <X className="w-4 h-4 mr-2" /> Limpiar{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
          </Button>
          <Button onClick={applyFilters} data-testid={ATID.auditApply}>Aplicar filtros</Button>
        </div>
      </div>

      <div className="bg-white border border-softblue rounded-md overflow-x-auto">
        <table data-testid={ATID.auditTable} className="w-full text-sm">
          <thead className="bg-lightgray/60 text-left text-[11px] uppercase tracking-widest text-textgray">
            <tr>
              <th className="py-3 px-4">Fecha</th>
              <th className="py-3 px-4">Usuario</th>
              <th className="py-3 px-4">Acción</th>
              <th className="py-3 px-4">Recurso</th>
              <th className="py-3 px-4">Detalle</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="p-6 text-center text-textgray"><Loader2 className="inline w-4 h-4 animate-spin mr-2" /> Cargando…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-textgray">No hay registros con esos filtros.</td></tr>
            )}
            {items.map((row) => (
              <tr key={row.id} data-testid={ATID.auditRow(row.id)} className="border-t border-softblue/60">
                <td className="px-4 py-3 whitespace-nowrap text-xs text-textgray">{formatDateTime(row.ts)}</td>
                <td className="px-4 py-3">
                  <div className="font-medium text-nightblue">{row.user_name || "—"}</div>
                  <div className="text-xs text-textgray">{row.user_email}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm">{ACTION_LABELS[row.action] || row.action}</div>
                  <div className="text-[10px] uppercase tracking-widest text-textgray">{row.action}</div>
                </td>
                <td className="px-4 py-3 text-xs">
                  <div className="uppercase tracking-widest text-textgray">{RESOURCE_LABELS[row.resource_type] || row.resource_type}</div>
                  <div className="font-mono truncate max-w-[220px]">{row.resource_id || ""}</div>
                </td>
                <td className="px-4 py-3 text-sm">
                  {row.resource_title && <div className="font-medium">{row.resource_title}</div>}
                  {row.metadata && Object.keys(row.metadata).length > 0 && (
                    <div className="text-xs text-textgray">
                      {Object.entries(row.metadata).map(([k, v]) => (
                        <span key={k} className="mr-2">{k}: <code>{JSON.stringify(v)}</code></span>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex justify-between items-center text-sm text-textgray">
        <span>{total.toLocaleString("es-AR")} eventos</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Anterior</Button>
          <Button variant="outline" size="sm" disabled={(page + 1) * pageSize >= total} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
        </div>
      </div>
    </div>
  );
}
